import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Reveal } from "@/components/pdp/reveal";
import { useProduct } from "@/components/pdp/PdpDataContext";

export const DetailsAccordion = () => {
  const P = useProduct();
  const d = P.details;
  const isV31 = !!P.v31;
  const OVERVIEW = { id: "overview", title: "Overview", content: <p className="leading-relaxed text-neutral-600">{P.shortDescription}</p> };
  const BENEFITS = {
    id: "benefits",
    title: "Key Benefits",
    content: (
      <ul className="space-y-2 text-neutral-600 leading-relaxed list-disc pl-5">
        {P.benefits.items.map((b, i) => <li key={i}>{b}</li>)}
      </ul>
    ),
  };
  const DETAILS = [
    // V3.1: pull Overview out of the accordion on desktop (shown as a
    // paragraph under the left heading instead). Overview STAYS as a tab
    // on mobile, positioned AFTER Key Benefits — the mobile-only visibility
    // is controlled by the `lg:hidden` className on the Overview item.
    ...(isV31 ? [BENEFITS, { ...OVERVIEW, mobileOnly: true }] : [OVERVIEW, BENEFITS]),
    // V3.1 hoists "How to Use" into a full-width editorial ritual section
    // above the reviews, so hide the accordion tab on V3.1.
    ...(isV31 ? [] : [{
      id: "how",
      title: "How to Use",
      content: (
        <ol className="space-y-3 text-neutral-600 leading-relaxed">
          {d.howTo.map((s, i) => (
            <li key={i} className="flex gap-4">
              <span className="font-mono text-riversol-blue text-xs pt-0.5">{String(i + 1).padStart(2, "0")}</span>
              <span>{s}</span>
            </li>
          ))}
        </ol>
      ),
    }]),
    {
      id: "ingredients",
      title: "Ingredients",
      content: <p className="leading-relaxed text-neutral-500 text-[13px]">{d.ingredients}</p>,
    },
    {
      id: "clinical",
      title: "Clinical Information",
      content: (
        <div>
          <div className="flex gap-10 mb-4">
            {P.clinical.stats.map((s) => (
              <div key={s.value}>
                <div className="font-mono text-2xl text-riversol-blue">{s.value}</div>
                <div className="text-xs text-neutral-500 max-w-[150px] mt-1">{s.label}</div>
              </div>
            ))}
          </div>
          <p className="pdp-clinical-disclaimer text-xs text-neutral-400 font-mono">* {P.clinical.disclaimer}</p>
        </div>
      ),
    },
    {
      id: "shipping",
      title: "Shipping and Returns",
      content: <p className="leading-relaxed text-neutral-600">{P.shipping} · {P.returns}. Orders are processed within 1–2 business days.</p>,
    },
  ];

  return (
    <section className="bg-white pt-12 pb-20 lg:pt-20 lg:pb-32" data-testid="details-section">
      <div className="max-w-[1600px] mx-auto px-4 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-4">
          <div className="pdp-eyebrow font-mono text-[11px] tracking-[0.24em] uppercase text-riversol-blue mb-6">
            {d.eyebrow}
          </div>
          <h2 className={`leading-[1.05] tracking-tight font-semibold text-neutral-900 ${d.big ? "text-3xl md:text-4xl lg:text-[3rem]" : "text-3xl md:text-4xl"}`}>
            {d.titleLines.map((l, i) => <span key={i} className="block">{l}</span>)}
          </h2>
          {isV31 && (
            <p
              className="hidden lg:block text-[15px] leading-relaxed text-neutral-600 mt-6 max-w-md"
              data-testid="details-overview-copy"
            >
              {P.shortDescription}
            </p>
          )}
        </div>
        <div className="lg:col-span-8">
          <Accordion type="multiple" defaultValue={isV31 ? ["benefits"] : ["overview"]} className="border-t border-neutral-200">
            {DETAILS.filter((item) => !(item.id === "clinical" && d.showClinical === false)).map((item) => (
              <AccordionItem
                key={item.id}
                value={item.id}
                className={`border-neutral-200 ${item.mobileOnly ? "lg:hidden" : ""}`}
              >
                <AccordionTrigger data-testid={`detail-${item.id}`} className={`font-normal text-neutral-900 hover:no-underline hover:text-riversol-blue ${d.big ? "py-8 font-mono text-2xl [&>svg]:h-6 [&>svg]:w-6" : "py-6 text-lg [&>svg]:h-5 [&>svg]:w-5"}`}>
                  {item.title}
                </AccordionTrigger>
                <AccordionContent className={`pr-8 ${d.big ? "pb-10 text-[17px] leading-[1.8]" : "pb-8 text-[15px]"}`}>{item.content}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
};

export const FaqSection = ({ headingRight = false }) => {
  const { faq } = useProduct();

  const accordion = (
    <Accordion type="multiple" className="border-t border-neutral-300">
      {faq.items.map((f, i) => (
        <AccordionItem key={i} value={`faq-${i}`} className="border-neutral-300">
          <AccordionTrigger data-testid={`faq-${i}`} className="py-6 text-lg font-normal text-neutral-900 hover:no-underline hover:text-riversol-blue text-left [&>svg]:h-5 [&>svg]:w-5">
            {f.q}
          </AccordionTrigger>
          <AccordionContent className="pb-8 pr-10 text-[15px] leading-relaxed text-neutral-600">
            {f.a}
          </AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );

  // Compact desktop variant (used on HP3): heading sits in a slim right column,
  // FAQ list fills the left — a tighter, more editorial footprint than the
  // centered/stacked PDP version. Falls back to stacked on mobile.
  if (headingRight) {
    return (
      <section className="bg-riversol-sand py-20 lg:py-28" data-testid="faq-section">
        <div className="max-w-[1300px] mx-auto px-4 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <Reveal>{accordion}</Reveal>
          </div>
          <div className="lg:col-span-5 order-1 lg:order-2 lg:text-right">
            <div className="lg:sticky lg:top-28">
              <div className="pdp-eyebrow font-mono text-[11px] tracking-[0.24em] uppercase text-riversol-blue mb-6">
                {faq.eyebrow}
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-[3rem] leading-[1.05] tracking-tight font-semibold text-neutral-900">
                {faq.title}
              </h2>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-riversol-sand py-20 lg:py-32" data-testid="faq-section">
      <div className="max-w-[1000px] mx-auto px-4 lg:px-8">
        <div className="text-center mb-12 lg:mb-16">
          <div className="pdp-eyebrow font-mono text-[11px] tracking-[0.24em] uppercase text-riversol-blue mb-6">
            {faq.eyebrow}
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-[3rem] leading-[1.05] tracking-tight font-semibold text-neutral-900">
            {faq.title}
          </h2>
        </div>
        <Reveal>{accordion}</Reveal>
      </div>
    </section>
  );
};
